import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  verifyBeforeUpdateEmail,
  fetchSignInMethodsForEmail,
  onAuthStateChanged,
  onIdTokenChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
  linkWithCredential,
  EmailAuthProvider,
  unlink,
} from 'firebase/auth';
import { doc, setDoc, getDoc, getDocFromServer, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../http/firebaseClient';
import { saveItem, saveObject, removeItem, getItem, getObject } from '../../utils/storage';
import { STORAGE_KEYS, API_BASE_URL } from '../../utils/constants';
import { logAccess } from './accessLogService';

const EMAIL_ACTION_CODE_SETTINGS = {
  url: process.env.REACT_APP_EMAIL_REDIRECT_URL || 'https://caralertbr.com/login?emailVerified=true',
  handleCodeInApp: false,
};

export const normalizeCPF = (cpf) => String(cpf || '').replace(/\D/g, '').slice(0, 11);

/**
 * Map Firebase Auth error codes to user-friendly Portuguese messages.
 * @param {string} code - Firebase error code (e.g. 'auth/wrong-password')
 * @returns {string} User-friendly error message
 */
const getFriendlyAuthError = (code) => {
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-password':
      return 'Senha incorreta. Verifique e tente novamente.';
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'Email não encontrado. Verifique o endereço digitado.';
    case 'auth/user-disabled':
      return 'Esta conta foi desativada. Entre em contato com o suporte.';
    case 'auth/too-many-requests':
      return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    case 'auth/network-request-failed':
      return 'Erro de conexão. Verifique sua internet e tente novamente.';
    case 'auth/email-already-in-use':
      return 'Este email já está em uso.';
    case 'auth/weak-password':
      return 'Senha muito fraca. Use pelo menos 6 caracteres.';
    case 'auth/popup-closed-by-user':
      return 'Login cancelado.';
    case 'auth/operation-not-allowed':
      return 'Provedor não habilitado no Firebase. Entre em contato com o suporte.';
    case 'auth/unauthorized-domain':
      return 'Domínio não autorizado no Firebase. Entre em contato com o suporte.';
    case 'auth/internal-error':
      return 'Erro interno de autenticação. Verifique sua conexão ou tente novamente.';
    case 'auth/account-exists-with-different-credential':
      return 'Já existe uma conta com este e-mail cadastrada com outro método de login. Faça login com o método original.';
    case 'auth/operation-not-supported-in-this-environment':
      return 'Este método de login não é suportado neste ambiente. Tente outro navegador.';
    case 'auth/web-storage-unsupported':
      return 'Armazenamento local bloqueado. Habilite cookies/armazenamento local no navegador.';
    case 'auth/redirect-cancelled-by-user':
      return 'Login cancelado.';
    case 'auth/redirect-operation-pending':
      return 'Aguarde — há um login por redirecionamento em andamento.';
    default:
      return `Erro ao fazer login. Tente novamente.${code ? ` [${code}]` : ''}`;
  }
};

/**
 * Register a new user via phone authentication (Firebase phone auth).
 * Must be called after verifyPhoneCode has signed in the user via SMS.
 * Saves CPF and name to Firestore.
 * @param {string} cpf - formatted CPF string (XXX.XXX.XXX-XX)
 * @param {string} cpfName - holder name from Receita Federal
 * @param {boolean} cpfVerified - whether CPF was verified against Receita Federal
 * @returns {Promise<object>} user data
 */
export const registerPhoneUser = async (cpf, cpfName, cpfVerified) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado via telefone.');

  const name = cpfName || '';

  // Update display name
  await updateProfile(user, { displayName: name });

  // Save user data to Firestore
  const userRef = doc(db, 'users', user.uid);
  await setDoc(userRef, {
    uid: user.uid,
    name,
    email: user.email || null,
    phoneNumber: user.phoneNumber || null,
    emailVerified: user.emailVerified,
    cpf: normalizeCPF(cpf),
    cpfVerified,
    cpfName,
    createdAt: new Date().toISOString(),
  }, { merge: true });

  const token = await user.getIdToken();
  await saveItem(STORAGE_KEYS.AUTH_TOKEN, token);

  const userData = {
    uid: user.uid,
    name,
    email: user.email || null,
    phoneNumber: user.phoneNumber || null,
    emailVerified: user.emailVerified,
  };
  await saveObject(STORAGE_KEYS.USER_DATA, userData);

  return userData;
};

/**
 * Link an email/password credential to the current phone-authenticated user.
 * Must be called after a successful phone sign-in (registerPhoneUser).
 * Sends an email verification link after linking.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<void>}
 */
export const linkEmailPasswordToPhoneUser = async (email, password) => {
  if (!auth) throw new Error('Firebase Auth não inicializado. Verifique a configuração do Firebase.');
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');

  const emailCredential = EmailAuthProvider.credential(email, password);
  try {
    await linkWithCredential(user, emailCredential);
  } catch (err) {
    if (err.code === 'auth/email-already-in-use') {
      const customErr = new Error('Este e-mail já está cadastrado. Use outro endereço ou faça login.');
      customErr.errorCode = 'email_already_registered';
      throw customErr;
    }
    if (err.code === 'auth/invalid-email') {
      throw new Error('Endereço de e-mail inválido. Verifique e tente novamente.');
    }
    if (err.code === 'auth/weak-password') {
      throw new Error('Senha muito fraca. Use pelo menos 6 caracteres.');
    }
    throw new Error(err.message || 'Erro ao vincular email. Tente novamente.');
  }

  // Update email in Firestore
  if (db) {
    const userRef = doc(db, 'users', user.uid);
    await setDoc(userRef, { email, emailVerified: false }, { merge: true });
  }

  // Send email verification link
  await sendEmailVerification(user, EMAIL_ACTION_CODE_SETTINGS);
};

/**
 * Register a new user with email and password.
 * @param {string} name
 * @param {string} email
 * @param {string} password
 * @param {string|null} [phoneNumber] - E.164 format, e.g. +5511999999999
 * @returns {Promise<object>} user data
 */
export const register = async (name, email, password, phoneNumber = null) => {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(credential.user, { displayName: name });
  try {
    const idToken = await credential.user.getIdToken();
    const response = await fetch(`${API_BASE_URL}/users/send-verification-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + idToken,
      },
    });
    if (!response.ok) {
      throw new Error('Falha ao solicitar envio do link de verificação');
    }
  } catch (verificationError) {
    console.warn('[car-alert] register: failed to send verification email', verificationError);
  }

  // Save user data to Firestore, including phoneNumber so phone-auth login can find this account
  const userRef = doc(db, 'users', credential.user.uid);
  await setDoc(userRef, {
    uid: credential.user.uid,
    name,
    email,
    phoneNumber: phoneNumber || null,
    emailVerified: false,
    createdAt: new Date().toISOString(),
  }, { merge: true });

  const token = await credential.user.getIdToken();
  await saveItem(STORAGE_KEYS.AUTH_TOKEN, token);

  const userData = {
    uid: credential.user.uid,
    name: credential.user.displayName,
    email: credential.user.email,
    phoneNumber: phoneNumber || null,
    emailVerified: credential.user.emailVerified,
  };
  await saveObject(STORAGE_KEYS.USER_DATA, userData);

  return userData;
};

/**
 * Sign in with email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>} user data
 */
export const login = async (email, password) => {
  let credential;
  try {
    credential = await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    console.error('[car-alert] Firebase auth error code:', err.code, err.message);
    await logAccess(null, 'password', false, err.code || 'auth-error');
    throw new Error(getFriendlyAuthError(err.code), { cause: err });
  }

  const token = await credential.user.getIdToken();
  await saveItem(STORAGE_KEYS.AUTH_TOKEN, token);

  const userData = {
    uid: credential.user.uid,
    name: credential.user.displayName,
    email: credential.user.email,
    emailVerified: credential.user.emailVerified,
  };
  await saveObject(STORAGE_KEYS.USER_DATA, userData);

  await logAccess(credential.user.uid, 'password', true);

  return userData;
};

/**
 * Sign out the current user.
 * Each step is wrapped individually so that a failure in one does not
 * prevent the others from running (e.g. signOut succeeds but storage removal
 * throws, or vice-versa).
 */
export const logout = async () => {
  try { await signOut(auth); } catch (err) { console.error('[car-alert] signOut failed:', err); }
  try { await removeItem(STORAGE_KEYS.AUTH_TOKEN); } catch (err) { console.error('[car-alert] removeItem AUTH_TOKEN failed:', err); }
  try { await removeItem(STORAGE_KEYS.USER_DATA); } catch (err) { console.error('[car-alert] removeItem USER_DATA failed:', err); }
};

/**
 * Get the currently stored user data.
 * @returns {Promise<object|null>}
 */
export const getCurrentUser = async () => {
  return getObject(STORAGE_KEYS.USER_DATA);
};

/**
 * Get the stored auth token.
 * @returns {Promise<string|null>}
 */
export const getToken = async () => {
  return getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Refresh the Firebase ID token for the current user.
 * @returns {Promise<string|null>}
 */
export const refreshToken = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  const token = await user.getIdToken(true);
  await saveItem(STORAGE_KEYS.AUTH_TOKEN, token);
  return token;
};

/**
 * Update the current user's display name.
 * @param {string} name
 */
export const updateUserName = async (name) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');
  await updateProfile(user, { displayName: name });

  const stored = await getObject(STORAGE_KEYS.USER_DATA);
  if (stored) {
    await saveObject(STORAGE_KEYS.USER_DATA, { ...stored, name });
  }
};

/**
 * Change the current user's password.
 * @param {string} newPassword
 */
export const changePassword = async (newPassword) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');
  await updatePassword(user, newPassword);
};

/**
 * Send a password reset email to the given address.
 * Callers should handle Firebase error codes such as
 * 'auth/user-not-found', 'auth/invalid-email', and
 * 'auth/too-many-requests' to present user-friendly messages.
 * @param {string} email
 * @returns {Promise<void>}
 */
export const resetPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

/**
 * Subscribe to auth state changes.
 * @param {function} callback
 * @returns {function} unsubscribe
 */
export const onAuthChange = (callback) => onAuthStateChanged(auth, callback);

/**
 * Subscribe to ID token changes (also fires after updateProfile + reload).
 * @param {function} callback
 * @returns {function} unsubscribe
 */
export const onIdTokenChange = (callback) => onIdTokenChanged(auth, callback);

/**
 * Resend the email verification link to the current user.
 * @returns {Promise<void>}
 */
export const resendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');
  if (!user.email) {
    console.warn('[car-alert] resendVerificationEmail: skipped for phone-authenticated user (no email address)');
    return;
  }
  const idToken = await user.getIdToken();
  const response = await fetch(`${API_BASE_URL}/users/send-verification-link`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + idToken,
    },
  });
  if (!response.ok) {
    throw new Error('Não foi possível reenviar o e-mail de verificação.');
  }
};

/**
 * Send a 6-digit verification code to the given email address.
 * Used during registration before the account is created.
 * @param {string} email
 * @returns {Promise<void>}
 * @throws {Error} with user-facing message on failure
 */
export const sendEmailRegistrationCode = async (email) => {
  const response = await fetch(`${API_BASE_URL}/users/send-email-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  const data = await response.json();

  if (!response.ok) {
    if (data.error === 'email_already_registered') {
      const err = new Error('Este e-mail já está cadastrado. Faça login ou utilize outro e-mail.');
      err.errorCode = 'email_already_registered';
      throw err;
    }
    if (data.error === 'email_bounced') {
      throw new Error('Este endereço de email não existe ou não pode receber emails. Verifique se digitou corretamente.');
    }
    if (data.error === 'email_suppressed') {
      throw new Error('Este email está temporariamente bloqueado. Por favor, use outro endereço de email.');
    }
    throw new Error(data.message || data.error || 'Erro ao enviar código por email');
  }
};

/**
 * Verify the 6-digit code sent to the given email address.
 * Used during registration before the account is created.
 * @param {string} email
 * @param {string} code
 * @returns {Promise<void>}
 * @throws {Error} with user-facing message on failure
 */
export const verifyEmailRegistrationCode = async (email, code) => {
  const response = await fetch(`${API_BASE_URL}/users/verify-email-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Código inválido. Verifique e tente novamente.');
  }
};

/**
 * Check whether a phone number is already registered.
 * @param {string} phoneNumber - E.164 format, e.g. +5511999999999
 * @param {string|null} [excludeUid] - When provided, a match belonging to this uid is ignored (e.g. profile edits)
 * @returns {Promise<void>} resolves if available, throws with user-facing message if already registered
 */
export const checkPhoneAvailability = async (phoneNumber, excludeUid = null) => {
  const response = await fetch(`${API_BASE_URL}/users/check-phone`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(excludeUid ? { phoneNumber, excludeUid } : { phoneNumber }),
  });

  let data;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new Error('Erro ao verificar número. Tente novamente.');
    }
    return;
  }

  if (!response.ok) {
    if (data.error === 'phone_already_registered') {
      throw new Error('Este número de celular já está cadastrado. Faça login ou use outro número.');
    }
    throw new Error(data.error || 'Erro ao verificar número. Tente novamente.');
  }
};

/**
 * Check whether a CPF is already registered.
 * @param {string} cpf - formatted CPF string (XXX.XXX.XXX-XX)
 * @returns {Promise<void>} resolves if available, throws with user-facing message if already registered
 */
export const checkCpfAvailability = async (cpf) => {
  const response = await fetch(`${API_BASE_URL}/users/check-cpf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cpf }),
  });

  let data;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new Error('Erro ao verificar CPF. Tente novamente.');
    }
    return;
  }

  if (!response.ok) {
    if (data.error === 'cpf_already_registered') {
      throw new Error('Este CPF já está cadastrado. Faça login ou use outro CPF.');
    }
    throw new Error(data.error || 'Erro ao verificar CPF. Tente novamente.');
  }
};

// ── Phone Auth ───────────────────────────────────────────────────────────────

/**
 * Configure an invisible reCAPTCHA verifier attached to the given container.
 * The container is cleared before rendering to prevent "already rendered" errors
 * on retries or when the component is remounted.
 * @param {string|HTMLElement} container - ID string or DOM element to attach to
 * @returns {RecaptchaVerifier}
 */
export const setupRecaptcha = (container) => {
  if (!auth) throw new Error('Firebase Auth não inicializado. Verifique a configuração do Firebase.');
  if (!auth.app) throw new Error('Firebase App não inicializado corretamente. Verifique a configuração do Firebase.');

  // Resolve the DOM element and clear it to prevent "reCAPTCHA has already been
  // rendered in this element" errors caused by duplicate or retry rendering.
  const el = typeof container === 'string' ? document.getElementById(container) : container;
  if (!el) {
    throw new Error(
      'Contêiner do reCAPTCHA não encontrado no DOM. ' +
      'Certifique-se de que o elemento com o ID correto está renderizado antes de chamar setupRecaptcha.'
    );
  }
  el.innerHTML = '';

  const createVerifier = () => new RecaptchaVerifier(auth, el, { size: 'invisible' });

  try {
    return createVerifier();
  } catch (err) {
    if (err.message && err.message.includes('already been rendered')) {
      // The reCAPTCHA widget is still registered globally; clear the element and retry once.
      el.innerHTML = '';
      try {
        return createVerifier();
      } catch (retryErr) {
        console.error('[car-alert] Erro ao criar RecaptchaVerifier (retry):', retryErr);
      }
    } else {
      console.error('[car-alert] Erro ao criar RecaptchaVerifier:', err);
    }
    throw new Error(
      'Não foi possível iniciar a verificação por telefone. ' +
      'Verifique se o provedor "Phone" está habilitado no Firebase Console e se a configuração do projeto está correta.'
    );
  }
};

/**
 * Send an SMS verification code to the given phone number.
 * @param {string} phoneNumber - E.164 format, e.g. +5511999999999
 * @param {RecaptchaVerifier} recaptchaVerifier
 * @returns {Promise<ConfirmationResult>}
 */
export const sendPhoneCode = async (phoneNumber, recaptchaVerifier) => {
  if (!auth) throw new Error('Firebase Auth não inicializado. Verifique a configuração do Firebase.');
  try {
    return await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
  } catch (err) {
    console.error('[car-alert] Erro ao enviar código SMS:', err);
    if (err.code === 'auth/invalid-app-credential' || err.code === 'auth/captcha-check-failed') {
      throw new Error('Erro de verificação reCAPTCHA. Recarregue a página e tente novamente.');
    }
    if (err.code === 'auth/too-many-requests') {
      throw new Error('Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.');
    }
    if (err.code === 'auth/invalid-phone-number') {
      throw new Error('Número de telefone inválido. Use o formato +55 (DDD) XXXXX-XXXX.');
    }
    if (err.code === 'auth/quota-exceeded') {
      throw new Error('Limite de SMS excedido. Tente novamente mais tarde.');
    }
    throw new Error(err.message || 'Erro ao enviar código SMS. Tente novamente.');
  }
};

/**
 * Verify the SMS code returned by sendPhoneCode.
 * @param {ConfirmationResult} confirmationResult
 * @param {string} code - 6-digit verification code
 * @returns {Promise<UserCredential>}
 */
export const verifyPhoneCode = async (confirmationResult, code) => {
  try {
    return await confirmationResult.confirm(code);
  } catch (err) {
    const errCode = err?.code;
    if (errCode === 'auth/code-expired') {
      throw new Error("O código expirou. Toque em 'Reenviar código' para receber um novo.");
    }
    if (errCode === 'auth/invalid-verification-code') {
      throw new Error('Código inválido. Verifique e tente novamente.');
    }
    if (errCode === 'auth/too-many-requests') {
      throw new Error('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
    }
    if (errCode === 'auth/session-expired') {
      throw new Error("A sessão de verificação expirou. Toque em 'Reenviar código' para começar novamente.");
    }
    throw new Error(err.message || 'Erro ao verificar código. Tente novamente.');
  }
};

// ── CPF (Firestore) ──────────────────────────────────────────────────────────

/**
 * Look up a Firestore user document by phoneNumber.
 * Used after phone OTP login to find the account originally created with email.
 * @param {string} phoneNumber - E.164 format, e.g. +5511999999999
 * @returns {Promise<object|null>} user data object or null if not found
 */
export const lookupUserByPhone = async (phoneNumber) => {
  if (!db || !phoneNumber) return null;
  const q = query(
    collection(db, 'users'),
    where('phoneNumber', '==', phoneNumber)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data();
};

/**
 * Save the user's CPF in the Firestore `users` collection.
 * @param {string} uid
 * @param {string} cpf - formatted CPF string (XXX.XXX.XXX-XX)
 * @param {boolean} [cpfVerified=false] - whether the CPF was verified against the Receita Federal
 * @param {string} [cpfName=''] - holder name returned by the Receita Federal scraping
 * @returns {Promise<void>}
 */
export const updateUserCPF = async (uid, cpf, cpfVerified = false, cpfName = '') => {
  if (!db) throw new Error('Firestore não inicializado');
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { cpf: normalizeCPF(cpf), cpfVerified, ...(cpfName ? { cpfName } : {}) }, { merge: true });
};

export const isCpfAlreadyRegisteredInFirestore = async (cpf, excludeUid = null) => {
  if (!db) throw new Error('Firestore não inicializado');
  const normalizedCpf = normalizeCPF(cpf);
  if (normalizedCpf.length !== 11) return false;

  const q = query(
    collection(db, 'users'),
    where('cpf', '==', normalizedCpf)
  );
  const snap = await getDocs(q);
  if (snap.empty) return false;
  if (!excludeUid) return true;
  return snap.docs.some((docSnap) => docSnap.id !== excludeUid);
};

/**
 * Save the user's phone number in the Firestore `users` collection and local storage.
 * @param {string} uid
 * @param {string} phoneNumber - E.164 format, e.g. +5511999999999
 * @returns {Promise<void>}
 */
export const updateUserPhone = async (uid, phoneNumber) => {
  if (!db) throw new Error('Firestore não inicializado');
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { phoneNumber: phoneNumber || null }, { merge: true });

  const stored = await getObject(STORAGE_KEYS.USER_DATA);
  if (stored) {
    await saveObject(STORAGE_KEYS.USER_DATA, { ...stored, phoneNumber: phoneNumber || null });
  }
};

export const getUserCPF = async (uid) => {
  if (!db) return { cpf: null, cpfName: null };
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return { cpf: null, cpfName: null };
  const data = snap.data();
  return { cpf: data.cpf || null, cpfName: data.cpfName || null };
};

/**
 * Fetch the user's phone number from the Firestore `users` collection.
 * Always fetches from the server (bypasses SDK cache) so that updates made on
 * other devices (e.g. the mobile app) are reflected immediately.
 * @param {string} uid
 * @returns {Promise<{ phone: string|null }>}
 */
export const getUserPhone = async (uid) => {
  if (!db) return { phone: null };
  // Use getDocFromServer to bypass the Firestore in-memory cache and always
  // get the latest value from the server. This ensures that a phone-number
  // update made on the mobile app is visible on the web without a full reload.
  const snap = await getDocFromServer(doc(db, 'users', uid));
  if (!snap.exists()) return { phone: null };
  const data = snap.data();
  // Prefer phoneNumber; fall back to legacy phone field for older documents.
  return { phone: data.phoneNumber || data.phone || null };
};

export const getUserAvatarStyle = async (uid) => {
  if (!db) return { avatarStyle: null };
  const snap = await getDocFromServer(doc(db, 'users', uid));
  if (!snap.exists()) return { avatarStyle: null };
  const data = snap.data();
  return { avatarStyle: data.avatarStyle || null };
};

export const updateUserAvatarStyle = async (uid, avatarStyle) => {
  if (!db) throw new Error('Firestore não inicializado');
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { avatarStyle: avatarStyle || null }, { merge: true });
};

/**
 * Save the user's chosen username in the Firestore `users` collection.
 * @param {string} uid
 * @param {string} username - chosen username (3–30 chars, letters/digits/_/-./)
 * @returns {Promise<void>}
 */
export const updateUserUsername = async (uid, username) => {
  if (!db) throw new Error('Firestore não inicializado');
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { username }, { merge: true });
};

/**
 * Get the user's chosen username from Firestore.
 * @param {string} uid
 * @returns {Promise<{ username: string|null }>}
 */
export const getUserUsername = async (uid) => {
  if (!db) return { username: null };
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return { username: null };
  const data = snap.data();
  return { username: data.username || null };
};

// ── CPF Situation (backend verification) ─────────────────────────────────────

/**
 * Verify the CPF cadastral status via the backend scraping endpoint.
 * @param {string} cpf - formatted or raw CPF
 * @returns {Promise<{ valid: boolean, situation: string, name: string }>}
 * @throws {Error} with user-facing message on network / service failure
 */
export const verifyCPFSituacao = async (cpf) => {
  const response = await fetch(`${API_BASE_URL}/users/verify-cpf`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cpf }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Erro ao verificar CPF');
  }

  return { valid: data.valid, situation: data.situation, name: data.name || '', verified: true };
};

/**
 * Check whether an email address is already registered in Firebase Auth.
 * @param {string} email - The email address to check
 * @returns {Promise<boolean>} true if the email is already in use, false otherwise
 */
export const isEmailAlreadyRegistered = async (email) => {
  const trimmed = String(email || '').trim().toLowerCase();
  if (!trimmed) return false;
  const methods = await fetchSignInMethodsForEmail(auth, trimmed);
  return Array.isArray(methods) && methods.length > 0;
};

/**
 * Check whether an email address is already registered in Firebase Auth.
 * Throws a user-facing error (PT-BR) if the email is taken.
 * @param {string} email
 * @returns {Promise<void>}
 */
export const checkEmailAvailability = async (email) => {
  const methods = await fetchSignInMethodsForEmail(auth, email);
  if (methods && methods.length > 0) {
    throw new Error('Este e-mail já está cadastrado. Faça login ou use outro endereço.');
  }
};

/**
 * Link an email address to a phone-authenticated account and send a verification email.
 * Uses verifyBeforeUpdateEmail so the email is only linked after the user confirms it.
 * @param {string} email - The email address to link
 * @returns {Promise<void>}
 */
export const addEmailToAccount = async (email) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');
  await verifyBeforeUpdateEmail(user, email, EMAIL_ACTION_CODE_SETTINGS);
};

/**
 * Link a password credential to the current user's account using their existing verified email.
 * Must be called after the user has verified their email (user.email must be set).
 * Also syncs the user's email and emailVerified status to Firestore.
 * @param {string} password - The password to set (min 6 characters)
 * @returns {Promise<void>}
 */
export const linkPasswordToAccount = async (password) => {
  if (!auth) throw new Error('Firebase Auth não inicializado. Verifique a configuração do Firebase.');
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');
  if (!user.email) throw new Error('Usuário não possui e-mail vinculado. Verifique o e-mail antes de criar a senha.');

  const credential = EmailAuthProvider.credential(user.email, password);
  try {
    await linkWithCredential(user, credential);
  } catch (err) {
    if (err.code === 'auth/email-already-in-use') {
      throw new Error('Este e-mail já está em uso por outra conta.');
    }
    if (err.code === 'auth/credential-already-in-use') {
      throw new Error('Esta credencial já está em uso por outra conta.');
    }
    if (err.code === 'auth/provider-already-linked') {
      throw new Error('Uma senha já está vinculada a esta conta.');
    }
    if (err.code === 'auth/weak-password') {
      throw new Error('Senha muito fraca. Use pelo menos 6 caracteres.');
    }
    if (err.code === 'auth/requires-recent-login') {
      throw new Error('Por segurança, faça login novamente antes de criar a senha.');
    }
    throw new Error(err.message || 'Erro ao criar senha. Tente novamente.');
  }

  // Sync email and verification status to Firestore so the account record is complete
  if (db) {
    const userRef = doc(db, 'users', user.uid);
    try {
      await setDoc(userRef, { email: user.email, emailVerified: user.emailVerified }, { merge: true });
    } catch (firestoreErr) {
      // Non-blocking: credential was already linked successfully; log for debugging
      console.warn('[car-alert] linkPasswordToAccount: Firestore sync failed:', firestoreErr);
    }
  }
};

/**
 * Start linking a phone number to the current user's Firebase Auth account via SMS.
 * Returns a ConfirmationResult that must be confirmed with the SMS code.
 * @param {string} phoneNumber - E.164 format, e.g. +5511999999999
 * @param {RecaptchaVerifier} recaptchaVerifier
 * @returns {Promise<ConfirmationResult>}
 */
export const linkPhoneToAccount = async (phoneNumber, recaptchaVerifier) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');
  return linkWithPhoneNumber(user, phoneNumber, recaptchaVerifier);
};

/**
 * Unlink the phone provider from the current user's Firebase Auth account.
 * @returns {Promise<void>}
 */
export const unlinkPhoneFromAccount = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado');
  await unlink(user, 'phone');
};
